import React, { useState, useEffect } from "react";
import { Label, TextInput } from "flowbite-react";
import { Clock } from "lucide-react";
import { toast } from "react-hot-toast";
import HeaderWithIcon from "../../components/common/HeaderWithIcon";
import LeftToRightPageWrapper from "../../components/common/LeftToRightPageWrapper ";
import { settingsApi } from "../../api/settings";
import { useAuth } from "../../context/AuthContext";
import ReferralSettings from "./ReferralSettings";
import PaymentGatewaySettings from "./PaymentGatewaySettings";

export default function Settings() {
  const { user, tokens } = useAuth();
  const [activeTab, setActiveTab] = useState("commission");
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");
  const [loading, setLoading] = useState(true);

  // Check if user is admin
  const isAdmin = user?.roleId === 1;

  useEffect(() => {
    // Fetch existing settings
    const fetchSettings = async () => {
      try {
        setLoading(true);
        
        // Get commission start time setting
        const startTimeResponse = await settingsApi.getSettings(
          tokens.access.token, 
          user.id, 
          "commissionConversionStartTime"
        );
        if (startTimeResponse.status === 200 && startTimeResponse.data) {
            console.log(startTimeResponse);
          setStartTime(startTimeResponse.data.value || "00:00");
        }
        
        // Get commission end time setting
        const endTimeResponse = await settingsApi.getSettings(
          tokens.access.token, 
          user.id, 
          "commissionConversionEndTime"
        );
        
        if (endTimeResponse.status === 200 && endTimeResponse.data) {
          setEndTime(endTimeResponse.data.value || "23:59");
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      } finally {
        setLoading(false);
      }
    };

    if (isAdmin) {
      fetchSettings();
    }
  }, [tokens.access.token, user.id, isAdmin]);

  const handleSaveSettings = async () => {
    if (!isAdmin) {
      toast.error("Only administrators can change these settings");
      return;
    }

    try {
      // Save start time
      const startTimeResponse = await settingsApi.settings(
        tokens.access.token,
        user.id,
        "commissionConversionStartTime",
        startTime
      );

      // Save end time
      const endTimeResponse = await settingsApi.settings(
        tokens.access.token,
        user.id,
        "commissionConversionEndTime",
        endTime
      );

      if (startTimeResponse.status === 201 && endTimeResponse.status === 201) {
        toast.success("Settings saved successfully");
      } else {
        toast.error("Failed to save some settings");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    }
  };

  const renderCommissionSettings = () => {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600 mb-6">
          Set the allowed time window for commission conversion to wallet
        </p>
        
        {/* Start Time */}
        <div>
          <Label className="block text-sm font-medium text-gray-700 mb-1">Start Time</Label>
          <div className="relative">
            <TextInput
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full"
              required
            />
            <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
          </div>
        </div>

        {/* End Time */}
        <div>
          <Label className="block text-sm font-medium text-gray-700 mb-1">End Time (Default: 12 Midnight)</Label>
          <div className="relative">
            <TextInput
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full"
              required
            />
            <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
          </div>
        </div>

        {/* Save Button */}
        <button
          type="button"
          onClick={handleSaveSettings}
          disabled={loading}
          className="w-full mt-6 p-2.5 bg-[#65758B] hover:bg-[#566275] text-white rounded-md font-medium"
        >
          {loading ? "Loading..." : "Save Settings"}
        </button>
      </div>
    );
  };

  const renderReferralSettings = () => {
    return (
      <div className="space-y-4">
        <ReferralSettings />
      </div>
    );
  };

  return (
    <LeftToRightPageWrapper>
      <div className="min-h-screen pb-16">
        <HeaderWithIcon title="Settings" />

        {!isAdmin ? (
          <div className="p-4 text-center text-red-500">
            Only administrators can access this page
          </div>
        ) : (
          <div className="p-4">
            {/* Tabs */}
            <div className="flex border-b mb-6">
              <button
                onClick={() => setActiveTab("commission")}
                className={`py-2 px-4 font-medium text-sm ${
                  activeTab === "commission"
                    ? "border-b-2 border-[#65758B] text-[#65758B]"
                    : "text-gray-500"
                }`}
              >
                Commission Conversion Settings
              </button>
              <button
                onClick={() => setActiveTab("referral")}
                className={`py-2 px-4 font-medium text-sm ${
                  activeTab === "referral"
                    ? "border-b-2 border-[#65758B] text-[#65758B]"
                    : "text-gray-500"
                }`}
              >
                Referral Settings
              </button>
              <button
                onClick={() => setActiveTab("paymentGateway")}
                className={`py-2 px-4 font-medium text-sm ${
                  activeTab === "paymentGateway"
                    ? "border-b-2 border-[#65758B] text-[#65758B]"
                    : "text-gray-500"
                }`}
              >
                Payment Gateway Selection
              </button>
            </div>

            {/* Tab content */}
            {activeTab === "commission"
              ? renderCommissionSettings()
              : activeTab === "referral"
              ? renderReferralSettings()
              : <PaymentGatewaySettings />}
          </div>
        )}
      </div>
    </LeftToRightPageWrapper>
  );
}
